import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
	NodeChangeType,
	ApolloQueryErrorMapper,
	CombiningSubscriptionManager,
	SubscriptionManagerService,
	ApolloQueryError
} from '@shure/shared/angular/data-access/system-api/core';
import { Response, ResponseState } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers } from '@shure/shared/typescript/rxjs';

import { LimiterApiService, LimiterChannel } from '../../api';
import {
	LimiterChannelMutationGQL,
	LimiterChannelQueryGQL,
	LimiterChannelSubscriptionGQL
} from '../graphql/generated/limiter.generated';
import { mapLimiterChannelFromSysApi } from '../mappers';

@Injectable()
export class DaiDeviceUiDspLimiterService implements LimiterApiService {
	private readonly logger: ILogger;

	private readonly limiterChannelSubscriptionManager: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly limiterChannelQueryGQL: LimiterChannelQueryGQL,
		private readonly limiterChannelMutationGQL: LimiterChannelMutationGQL,
		limiterChannelSubscriptionGQL: LimiterChannelSubscriptionGQL,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('DaiDeviceUiDspLimiterService');

		this.limiterChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiDeviceUiDspLimiterService',
				changeTypes: [NodeChangeType.AudioChannelLimiter, NodeChangeType.AudioChannelChannelConfiguration],
				subscriptionGql: limiterChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getChannel$(channelId: string): Observable<LimiterChannel> {
		const queryId = uuidv4();
		this.logger.trace('getChannel$()', 'Querying limiter channel', { channelId, queryId });

		return this.limiterChannelQueryGQL.watch({ channelId }, { fetchPolicy: 'network-only' }).valueChanges.pipe(
			map((query) => {
				if (query.data?.node && 'features' in query.data.node) {
					this.logger.trace('getChannel$()', 'Query updated', { channelId });
					return mapLimiterChannelFromSysApi(query.data.node);
				}

				this.logger.error('getChannel$()', 'Failed to query channel', { channelId });
				throw ApolloQueryErrorMapper.getError(query);
			}),
			tap((channel) => this.limiterChannelSubscriptionManager.subscribe(queryId, channel.id)),
			noMoreSubscribers(() => {
				this.logger.trace('getChannel$', 'noMoreSubscribers', { channelId, queryId });
				this.limiterChannelSubscriptionManager.unsubscribeQuery(queryId);
			}),
			catchError((error) => {
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public toggleLimiter(channelId: string, enabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleLimiter()', 'Toggling limiter', { channelId, enabled });
		return this.limiterChannelMutationGQL
			.mutate(
				{
					updates: {
						audioChannel: {
							id: channelId,
							features: {
								limiter: { enabled }
							}
						}
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((mutation) => {
					if (
						mutation.data?.updateNodes &&
						'error' in mutation.data.updateNodes[0] &&
						mutation.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateChannel()', 'Failed to update channel', {
							channelId,
							mutation
						});
						return { state: ResponseState.Error, error: mutation.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateChannel()', 'Done', { channelId });
					return { state: ResponseState.Done };
				}),
				catchError((error) => {
					this.logger.error('updateChannel()', 'Failed to update channel', { error });
					return of({ state: ResponseState.Error });
				})
			);
	}

	public setThreshold(channelId: string, threshold: number): Observable<Response<void, string>> {
		this.logger.trace('setThreshold()', 'Setting threshold', { channelId, threshold });
		return this.limiterChannelMutationGQL
			.mutate(
				{
					updates: {
						audioChannel: {
							id: channelId,
							features: {
								limiter: { threshold }
							}
						}
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((mutation) => {
					if (
						mutation.data?.updateNodes &&
						'error' in mutation.data.updateNodes[0] &&
						mutation.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateChannel()', 'Failed to update channel', {
							channelId,
							mutation
						});
						return { state: ResponseState.Error, error: mutation.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateChannel()', 'Done', { channelId });
					return { state: ResponseState.Done };
				}),
				catchError((error) => {
					this.logger.error('updateChannel()', 'Failed to update channel', { error });
					return of({ state: ResponseState.Error });
				})
			);
	}
}
